import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowUpLeft, Calendar, BookOpen } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import { t } from "../../i18n";
import api from "../../api/client";
import logger from "../../utils/logger";
import LoadingScreen from "../ui/Loading/Loading";
import Breadcrumb from "../ui/Breadcrumb/Breadcrumb";
import "./BlogList.css";

function useTranslation(lang) {
  return useCallback(
    (key, arFallback, enFallback) => {
      const translated = t(lang, key);
      if (translated && translated !== key) return translated;
      return lang === "ar" ? arFallback : enFallback;
    },
    [lang]
  );
}

function formatDate(dateString, lang) {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    if (lang === "ar") {
      return date.toLocaleDateString("ar-SA", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    }
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return dateString;
  }
}

function timeAgo(dateString, lang) {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

    if (lang === "ar") {
      if (diffDays > 30) return formatDate(dateString, "ar");
      if (diffDays > 0) return `منذ ${diffDays} يوم${diffDays > 1 ? "ات" : ""}`;
      if (diffHours > 0) return `منذ ${diffHours} ساعة`;
      return "منذ قليل";
    } else {
      if (diffDays > 30) return formatDate(dateString, "en");
      if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
      if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
      return "Just now";
    }
  } catch {
    return dateString;
  }
}

function truncateToWord(text, maxLength = 200) {
  if (!text || text.length <= maxLength) return text;

  const slice = text.slice(0, maxLength);

  let lastSpace = -1;
  for (let i = slice.length - 1; i >= 0; i--) {
    const code = slice.charCodeAt(i);
    if (slice[i] === " " || slice[i] === "\t" || slice[i] === "\n" || slice[i] === "\r" || code === 160) {
      lastSpace = i;
      break;
    }
  }

  if (lastSpace === -1) {
    return slice.trimEnd() + " ...";
  }

  return slice.slice(0, lastSpace).trimEnd() + " ...";
}

export default function BlogList() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { lang } = useLanguage();
  const isRTL = lang === "ar";
  const tx = useTranslation(lang);

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        const data = await api.apiGet(`blog?lang=${encodeURIComponent(lang)}`);
        setPosts(data?.status === "success" && Array.isArray(data.posts) ? data.posts : []);
      } catch (e) {
        logger.error("Error loading blog posts", e);
        setPosts([]);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [lang, tx]);

  if (loading) {
    return (
      <div className="blog-page" dir={isRTL ? "rtl" : "ltr"}>
        <div className="blog-container">
          <LoadingScreen lang={lang} />
        </div>
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className="blog-page" dir={isRTL ? "rtl" : "ltr"}>
        <Breadcrumb items={[{ label: tx("blog", "المدونة", "Blog") }]} />
        <div className="blog-container">
          <div className="blog-empty">
            <div className="blog-empty-icon">
              <BookOpen size={48} strokeWidth={1.5} />
            </div>
            <h2>{tx("blog", "المدونة", "Blog")}</h2>
            <p>{tx("no_posts", "لا توجد مقالات حالياً.", "No blog posts yet.")}</p>
          </div>
        </div>
      </div>
    );
  }

  const ArrowIcon = isRTL ? ArrowUpLeft : ArrowUpRight;

  return (
    <div className="blog-page" dir={isRTL ? "rtl" : "ltr"}>
      <Breadcrumb items={[{ label: tx("blog", "المدونة", "Blog") }]} />

      <header className="blog-header">
        <div className="blog-header-content">
          <div className="blog-title-wrapper">
            <div className="blog-header-icon">
              <BookOpen size={24} strokeWidth={2} />
            </div>
            <div className="blog-title-group">
              <h1 className="blog-header-title">
                {tx("blog", "المدونة", "Blog")}
              </h1>
            </div>
          </div>
        </div>
      </header>

      <div className="blog-container">
        <div className="blog-subheader">
          <p className="blog-subheader-text">
            {tx(
              "blog_subheader",
              "اكتشف مجموعة مختارة من المقالات والتحليلات العميقة التي تقدمها لك مجموعة موسى، حيث نسلط الضوء على أحدث التطورات في عالم التجارة والتقنية والأعمال، ونقدم لك رؤى استراتيجية ملهمة تساعدك على اتخاذ قرارات واعية وبناء مستقبل مزدهر.",
              "Discover a curated collection of articles and deep dives brought to you by Mousa Group, where we highlight the latest developments in commerce, technology, and business, and provide inspiring strategic insights to help you make informed decisions and build a prosperous future."
            )}
          </p>
        </div>

        {posts.length > 0 && (
          <section className="blog-grid-section">
            <div className="blog-grid-connected">
              {posts.map((post, index) => {
                const imageSrc = post?.image_url ? api.buildUrl(post.image_url) : "";
                const hasImage = !!imageSrc;

                return (
                  <article
                    key={post.id}
                    className={`blog-tile ${hasImage ? "has-image" : "no-image"}`}
                    style={{ animationDelay: `${index * 60}ms` }}
                  >
                    <Link to={`/blog/${post.slug}`} className="blog-tile-inner">
                      <div className="blog-tile-image-area">
                        {hasImage ? (
                          <img src={imageSrc} alt={post.title} loading="lazy" />
                        ) : (
                          <div className="blog-tile-placeholder">
                            <BookOpen size={48} strokeWidth={1.5} />
                          </div>
                        )}
                      </div>

                      <div className="blog-tile-content">
                        <div className="blog-tile-meta">
                          <span className="blog-tile-date">
                            <Calendar size={14} />
                            {timeAgo(post.created_at, lang)}
                          </span>
                        </div>

                        <h3 className="blog-tile-title">{post.title}</h3>

                        {post.excerpt && post.excerpt !== post.title && (
                          <p className="blog-tile-excerpt">
                            {truncateToWord(post.excerpt, 200)}
                          </p>
                        )}

                        <span className="blog-tile-link">
                          <span>{tx("continue_reading", "أكمل القراءة", "Continue reading")}</span>
                          <ArrowIcon size={14} strokeWidth={2.5} />
                        </span>
                      </div>
                    </Link>
                  </article>
                );
              })}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}